// Auth store (Pinia) – Vue 3 + Supabase v2
// Responsibilities:
// - Expose current user/session state and a minimal role (UI only)
// - Subscribe to Supabase auth events and keep state in sync
// - Load role via secure RPC (server-side authorization lives in Postgres/RLS)
// - Handle navigation/notifications on sign-in/sign-out
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import type { User } from '@supabase/supabase-js'
import { supabase } from '@/utils/supabase'
import { mapAuthError } from '@/utils/authErrorMessages'
import { useNotificationsStore } from './notifications'
import type { Router } from 'vue-router'

let _router: Router | null = null
async function getRouter() {
  if (!_router) {
    const mod = await import('@/router')
    _router = (mod as any).default as Router
  }
  return _router!
}

// Rate limiting côté client (amélioration UX uniquement)
// NOTE: La vraie protection anti-brute-force est gérée par Supabase Auth côté serveur (rate limit par IP).
// Ce mécanisme client évite des appels API inutiles et améliore le feedback utilisateur.
const LOGIN_MAX_ATTEMPTS = 5
const LOGIN_LOCKOUT_MS = 15 * 60 * 1000 // 15 minutes
let loginAttempts = 0
let loginLockoutUntil = 0

// NOTE: This store starts the Supabase auth listener once (guarded against HMR duplicates)
export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const loading = ref(true)
  const userRole = ref<number | null>(null)
  // Optional profile data from DB (preferred over metadata for display)
  const userFullName = ref<string | null>(null)
  const mustChangePassword = ref<boolean | null>(null)
  const wasAuthenticated = ref(false)
  let isVoluntaryLogout = false
  const notifications = useNotificationsStore()
  let unsub: (() => void) | null = null

  // Defensive bootstrap: ensure we hydrate session/role even if INITIAL_SESSION never arrives
  let bootstrapped = false
  async function bootstrapSession() {
    if (bootstrapped) return
    bootstrapped = true
    try {
      const { data } = await supabase.auth.getSession()
      user.value = data?.session?.user ?? null
      if (user.value) {
        // Drop loading first, fetch profile in background
        setTimeout(() => {
          fetchMe().catch((e) => console.error('[auth] me() fetch error', e))
        }, 0)
      } else {
        userRole.value = null
        userFullName.value = null
        mustChangePassword.value = null
      }
    } catch (e) {
      console.error('[auth] bootstrap error', e)
    } finally {
      // Make sure UI can render even if no auth event came through
      loading.value = false
    }
  }

  // State:
  // - user: current Supabase user (null if signed-out)
  // - loading: true while an auth event is being processed (incl. role fetch)
  // - userRole: small UI-oriented role (1|2) loaded from DB via RPC
  // - wasAuthenticated: helps detect unexpected sign-outs (token expiration)
  // - unsub: holds the onAuthStateChange unsubscribe function (HMR-safe)

  // Route helpers: skip redirects/notifications while on auth pages or during recovery flow
  const isAuthPage = () => window.location.pathname.startsWith('/auth/')
  const isResetFlow = () => (window.location.hash ?? '').includes('type=recovery')

  // Best practice: fetch role via a secure RPC (server filters by auth.uid())
  // The role here is for UI; real authorization must be enforced by RLS/SQL
  async function fetchMe() {
    const { data, error } = await supabase.rpc('me')
    if (error || !data) {
      userRole.value = null
      userFullName.value = null
      mustChangePassword.value = null
      return
    }
    const row = Array.isArray(data) ? data[0] : data
    userFullName.value = row?.full_name ?? null
    userRole.value = row?.role ?? null
    mustChangePassword.value = row?.must_change_password ?? null
  }

  // Back-compat: keep old name used elsewhere
  async function fetchUserRole() { return fetchMe() }

  // Subscribes to Supabase auth events. Guard against duplicate subscriptions in HMR.
  function startAuthListener() {
    if (unsub) return
    // Kick a bootstrap in parallel in case INITIAL_SESSION never fires
    void bootstrapSession()
    const { data: sub } = supabase.auth.onAuthStateChange(async (event, session) => {
      const prev = user.value
      try {
        // Keep local user state in sync with Supabase session
        user.value = session?.user ?? null

        if (event === 'INITIAL_SESSION' && session?.user) {
          wasAuthenticated.value = true
        }

        // Drop loading as soon as session state is known; role loads in background
        loading.value = false

        // Load role on initial page load and immediately after a successful sign-in (non-blocking)
        if (session?.user && (event === 'INITIAL_SESSION' || event === 'SIGNED_IN')) {
          fetchMe().catch((e) => console.error('[auth] me() fetch error', e))
        }
        if (!session?.user) { userRole.value = null; userFullName.value = null; mustChangePassword.value = null }

        const goLogin = async () => {
          const r = await getRouter()
          if (r.currentRoute.value.name !== 'login') {
            await r.push({ name: 'login' })
          }
        }

        // Do not redirect or notify while the user is on the auth/reset screens
        const skipRedirect = isAuthPage() || isResetFlow()

        // Minimal UX rules on auth transitions (no spam on token refresh)
        switch (event) {
          case 'SIGNED_IN':
            wasAuthenticated.value = true
            break
          case 'SIGNED_OUT':
            if (isVoluntaryLogout) {
              notifications.addNotification('Vous avez été déconnecté', 'info')
            } else if (wasAuthenticated.value && prev) {
              notifications.addNotification('Session expirée, veuillez vous reconnecter', 'warning')
            }
            isVoluntaryLogout = false
            wasAuthenticated.value = false
            userFullName.value = null
            mustChangePassword.value = null
            if (!skipRedirect) {
              await goLogin()
            }
            break
        }
      } finally {
        // no-op: loading cleared earlier to avoid blocking on RPC
      }
    })
    unsub = () => sub.subscription.unsubscribe()
  }

  // HMR cleanup: on module replace, drop previous subscription to avoid duplicate listeners
  if (import.meta.hot) {
    import.meta.hot.accept(() => { if (unsub) { unsub(); unsub = null } })
  }

  // Start the auth subscription as soon as the store is created
  startAuthListener()

  // Programmatic way to force sign-out UX (e.g., 401 from an API): reset state + go to login
  const handleSessionLost = async () => {
    user.value = null
    userRole.value = null
    userFullName.value = null
    mustChangePassword.value = null
    wasAuthenticated.value = false
    notifications.addNotification('Session expirée, veuillez vous reconnecter', 'warning')
    const r = await getRouter()
    if (r.currentRoute.value.name !== 'login') {
      await r.push({ name: 'login' })
    }
  }

  // Email/password login: also fetch role immediately (do not wait for the auth event)
  const login = async (email: string, password: string, redirectTo?: string) => {
    // Rate limiting côté client
    const now = Date.now()
    if (now < loginLockoutUntil) {
      const remainingMins = Math.ceil((loginLockoutUntil - now) / 60000)
      throw new Error(`Trop de tentatives. Réessayez dans ${remainingMins} minute(s).`)
    }

    loading.value = true
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password })
      if (error) {
        loginAttempts++
        if (loginAttempts >= LOGIN_MAX_ATTEMPTS) {
          loginLockoutUntil = now + LOGIN_LOCKOUT_MS
          loginAttempts = 0
        }
        throw error
      }
      // Succès : reset compteur
      loginAttempts = 0
      user.value = data.user
      wasAuthenticated.value = true
      loading.value = false
      fetchMe().catch((e) => console.error('[auth] me() fetch error', e))
      const r = await getRouter()
      // Utiliser le redirect s'il est fourni, sinon aller vers Projets
      const destination = redirectTo || '/projects'
      if (r.currentRoute.value.fullPath !== destination) {
        await r.push(destination)
      }
    } catch (e: unknown) {
      // Pas de notification ici, la vue affiche l'erreur inline
      throw e
    } finally {
      loading.value = false
    }
  }

  // Sign out: onAuthStateChange will handle notifications and navigation
  const logout = async () => {
    loading.value = true
    isVoluntaryLogout = true
    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
      // Mettre à jour l'état immédiatement pour que les guards fonctionnent
      user.value = null
      userRole.value = null
      userFullName.value = null
      mustChangePassword.value = null
    } catch (e: unknown) {
      isVoluntaryLogout = false
      notifications.addNotification(mapAuthError(e, 'Erreur lors de la déconnexion'), 'error')
      throw e
    } finally {
      loading.value = false
    }
  }

  // Send password reset email; redirects back to /auth/reset-password after link
  const resetPassword = async (email: string) => {
    loading.value = true
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`
      })
      if (error) throw error
      notifications.addNotification(
        'Instructions envoyées par email. Vous recevrez un lien de réinitialisation.',
        'success'
      )
    } catch (e: unknown) {
      // Pas de notification ici, la vue affiche l'erreur inline
      throw e
    } finally {
      loading.value = false
    }
  }

  // Update password for the current user, then route to login
  const updatePassword = async (password: string) => {
    loading.value = true
    try {
      const { error } = await supabase.auth.updateUser({ password })
      if (error) throw error
      notifications.addNotification('Mot de passe mis à jour avec succès', 'success')
      const r = await getRouter()
      await r.push({ name: 'login' })
    } catch (e: unknown) {
      // Pas de notification ici, la vue affiche l'erreur inline
      throw e
    } finally {
      loading.value = false
    }
  }

  // Wait for auth store to be ready (with timeout)
  const ensureReady = async (maxWaitMs = 2000) => {
    if (!loading.value) return true

    return new Promise<boolean>((resolve) => {
      const start = Date.now()

      // If already ready, resolve immediately
      if (!loading.value) {
        resolve(true)
        return
      }

      const timeout = setTimeout(async () => {
        console.warn('Auth store ensureReady timed out after', Date.now() - start, 'ms — forcing bootstrap')
        try {
          const { data, error } = await supabase.auth.getSession()
          if (!error) {
            user.value = data.session?.user ?? null
            if (user.value) {
              // Fetch profile in background to avoid blocking UI
              fetchMe().catch((e) => console.error('[auth] me() fetch error', e))
            } else {
              userRole.value = null
              userFullName.value = null
              mustChangePassword.value = null
            }
          }
        } catch (e) {
          console.error('ensureReady bootstrap error', e)
        } finally {
          loading.value = false
          resolve(true)
        }
      }, maxWaitMs)

      const unwatch = watch(loading, (isLoading) => {
        if (!isLoading) {
          clearTimeout(timeout)
          unwatch()
          resolve(true)
        }
      }, { immediate: true })
    })
  }

  return {
    user,
    loading,
    userRole,
    userFullName,
    mustChangePassword,
    isAuthenticated: computed(() => !!user.value),
    setUser: (u: User | null) => (user.value = u),
    setUserRole: (r: number | null) => (userRole.value = r),
    login,
    logout,
    resetPassword,
    updatePassword,
    handleSessionLost,
    // Exposed for rare cases (e.g., manual refresh after profile changes)
    fetchMe,
    fetchUserRole,
    ensureReady
  }
})